/**
 * Skeleton Loaders for Premium Loading States
 * Used across Home, Category, and Product pages
 */
import { Skeleton } from '@/components/ui/skeleton';

// Product Card Skeleton
export function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
      <Skeleton className="aspect-square w-full" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <div className="flex justify-between items-center pt-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      </div>
    </div>
  );
}

// Product Grid Skeleton
export function ProductGridSkeleton({ count = 8 }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

// Home Page Hero Skeleton
export function HeroSkeleton() {
  return (
    <div className="relative h-[60vh] min-h-[400px] bg-gray-100">
      <Skeleton className="absolute inset-0" />
      <div className="absolute inset-0 flex flex-col items-center justify-center p-8 space-y-4">
        <Skeleton className="h-12 w-3/4 max-w-lg" />
        <Skeleton className="h-6 w-1/2 max-w-md" />
        <Skeleton className="h-12 w-40 rounded-full mt-4" />
      </div>
    </div>
  );
}

// Category Banner Skeleton
export function CategoryBannerSkeleton() {
  return (
    <div className="relative h-48 md:h-64 bg-gray-100 rounded-xl overflow-hidden">
      <Skeleton className="absolute inset-0" />
      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>
    </div>
  );
}

// Category Grid Skeleton
export function CategoryGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col items-center space-y-3">
          <Skeleton className="w-24 h-24 md:w-32 md:h-32 rounded-xl" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

// Product Detail Skeleton
export function ProductDetailSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Image Gallery */}
        <div className="space-y-4">
          <Skeleton className="aspect-square rounded-xl" />
          <div className="flex gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="w-20 h-20 rounded-lg" />
            ))}
          </div>
        </div>
        
        {/* Product Info */}
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
          
          <div className="flex items-baseline gap-3">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-5 w-16" />
          </div>
          
          <Skeleton className="h-px w-full" />
          
          <div className="space-y-3">
            <Skeleton className="h-4 w-16" />
            <div className="flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-20 rounded-lg" />
              ))}
            </div>
          </div>
          
          <div className="flex gap-3">
            <Skeleton className="h-12 flex-1 rounded-lg" />
            <Skeleton className="h-12 w-12 rounded-lg" />
          </div>
          
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Cart Item Skeleton
export function CartItemSkeleton() {
  return (
    <div className="flex gap-4 p-4 bg-white rounded-lg">
      <Skeleton className="w-24 h-24 rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <div className="flex justify-between items-center pt-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
    </div>
  );
}

// Order Card Skeleton
export function OrderCardSkeleton() {
  return (
    <div className="bg-white rounded-lg border p-4 space-y-4">
      <div className="flex justify-between items-start">
        <div className="space-y-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <div className="flex gap-3">
        <Skeleton className="w-16 h-16 rounded" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    </div>
  );
}

// Home Page Full Skeleton
export function HomePageSkeleton() {
  return (
    <div className="space-y-12">
      <HeroSkeleton />
      <div className="container mx-auto px-4 space-y-12">
        <section className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <CategoryGridSkeleton count={6} />
        </section>
        <section className="space-y-6">
          <Skeleton className="h-8 w-56" />
          <ProductGridSkeleton count={4} />
        </section>
      </div>
    </div>
  );
}

// Category Page Skeleton
export function CategoryPageSkeleton() {
  return (
    <div className="space-y-8">
      <CategoryBannerSkeleton />
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-40" />
        </div>
        <ProductGridSkeleton count={12} />
      </div>
    </div>
  );
}

export default {
  ProductCardSkeleton,
  ProductGridSkeleton,
  HeroSkeleton,
  CategoryBannerSkeleton,
  CategoryGridSkeleton,
  ProductDetailSkeleton,
  CartItemSkeleton,
  OrderCardSkeleton,
  HomePageSkeleton,
  CategoryPageSkeleton,
};
